import React, { useEffect } from "react"
import { Typography, Box } from "@carrotfertility/carotene-core"
import { useStateMachine } from "#/components/context/stateMachine/StateMachineContext"
import useInitialSetup from "#/components/cmd-enrollment/hooks/useInitialSetup"
import { Steps } from "#/components/cmd-enrollment/workflow/steps"
import { CenteredLoadingIndicator } from "#/components/views/molecules/Molecules"
import { useUserDeductible } from "../../../../context/user/DeductibleContext"
import { useSelector } from "react-redux"
import { getReimbursementDetails } from "#/redux/reducers/reimbursementDetails"
import { getUserInfo } from "#/redux/reducers/userInfo"
import { FormattedMessage } from "react-intl"
import { carrotClient } from "#/utils/CarrotClient"
import { useProgressTracker } from "../../../../views/modal/ProgressTracker"

interface ProcessConnectInsuranceStepProps {
  viewName: typeof Steps[keyof typeof Steps]
  handleVerifyInsurance: () => Promise<boolean>
}

function ProcessConnectInsuranceStep({
  viewName,
  handleVerifyInsurance
}: ProcessConnectInsuranceStepProps): JSX.Element {
  const { refreshUserBenefitEnrollment } = useUserDeductible()
  const reimbursementDetails = useSelector(getReimbursementDetails)
  const userInfo = useSelector(getUserInfo)
  const { setNextStep } = useStateMachine(viewName, null)

  const { showProgress } = useProgressTracker()
  useEffect(showProgress, [showProgress])

  useInitialSetup(async () => {
    const success = await handleVerifyInsurance()
    await refreshUserBenefitEnrollment()

    const knownTradingPartnerOutages = reimbursementDetails.knownTradingPartnerOutages
    const selectedPartner = userInfo.selectedTradingPartnerId
    const knownOutage = knownTradingPartnerOutages.includes(selectedPartner)

    if (!success && knownOutage) {
      setNextStep(viewName, "KNOWN_OUTAGE")
    } else {
      setNextStep(viewName, success)
    }
  })

  return (
    <Box display="flex" alignItems="center" flexDirection="column" paddingY="2.25rem">
      <CenteredLoadingIndicator />
      <Typography
        id="step-heading"
        variant="h2"
        color={(theme) => theme.palette.text.primary}
        paddingBottom={(theme) => theme.tokens.spacing.lg}
      >
        <FormattedMessage defaultMessage="We're working on your request." />
      </Typography>
    </Box>
  )
}

export function MemberProcessConnectInsuranceStep(): JSX.Element {
  const viewName = Steps.MEMBER_PROCESS_CONNECT_INSURANCE

  function handleVerifyInsurance(): Promise<boolean> {
    return carrotClient.verifyMemberInsurance()
  }

  return <ProcessConnectInsuranceStep {...{ viewName, handleVerifyInsurance }} />
}

export function PartnerProcessConnectInsuranceStep(): JSX.Element {
  const viewName = Steps.PARTNER_PROCESS_CONNECT_INSURANCE

  function handleVerifyInsurance(): Promise<boolean> {
    return carrotClient.verifyPartnerInsurance()
  }

  return <ProcessConnectInsuranceStep {...{ viewName, handleVerifyInsurance }} />
}

export {
  PartnerConnectSuccess as PartnerProcessConnectInsuranceSuccessStep,
  MemberConnectSuccess as MemberProcessConnectInsuranceSuccessStep
} from "./Success"
